import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Scale, X } from 'lucide-react';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';
import { locationIcon } from '../../Sidebar/DispenserComponents/LocationChoiceModal';
import { MERMA_TOLERANCIA_G, mermaDe } from '../../../lib/helpers/products';

/**
 * «Pesar antes de servir» — la respuesta de la Barra al 400 `weights_required`.
 *
 * El contrato (U0, comandas a peso): una comanda con líneas a peso NO se cobra
 * sin báscula — el stock descuenta el peso REAL y la merma queda registrada,
 * igual que en la venta del dispensario. El backend rechaza sin peso y también
 * fuera de tolerancia; este modal es el aviso temprano para que el viaje no
 * sea a ciegas: valida ±0,10 g EN LA PANTALLA (la misma constante compartida),
 * y el servidor vuelve a validar (el FE avisa, el backend manda).
 *
 * Misma liturgia que el diálogo de ubicaciones: cerrar sin pesar deja la
 * comanda EXACTAMENTE igual (viva, sin cobrar) y el caller lo cuenta en la
 * tarjeta — nunca una comanda atascada sin poder contestar.
 *
 * MODO COMBINADO (petición CRUO 23-08: «esos 2 en el mismo modal»): si la
 * comanda a peso además necesita elegir nevera (409 location_choice_required),
 * la pregunta «¿De dónde sale?» aparece AQUÍ DENTRO como sección, no como un
 * segundo popup encadenado — báscula y ubicación se confirman de un toque.
 * `ubicacion` = el detail del 409; onConfirm entrega (weights, locationId).
 */
export default function BasculaModal({
  active,
  lineas = [],
  busy = false,
  toleranciaG = MERMA_TOLERANCIA_G,
  ubicacion = null,
  onClose,
  onConfirm,
}) {
  const overlayDismiss = useOverlayDismiss(onClose);
  const [pesos, setPesos] = useState({});
  const [neveraElegida, setNeveraElegida] = useState(null);

  // Pre-carga al ABRIR (transición cerrada→abierta, no en cada render: `lineas`
  // cambia de identidad y resetearía lo tecleado): la báscula que el POS de Sala
  // apuntó en el carrito llega en `real_weight` y aparece ya escrita — la barra
  // solo confirma, o la corrige si el corte real fue otro. Petición CRUO 23-08:
  // «en Servida los datos que se hayan introducido, y la posibilidad de cambiarlos».
  const estabaActivo = useRef(false);
  useEffect(() => {
    if (active && !estabaActivo.current) {
      const semilla = {};
      (lineas || []).forEach((l) => {
        if (l.real_weight != null) semilla[l.order_item_id] = String(l.real_weight);
      });
      setPesos(semilla);
      setNeveraElegida(null);
    }
    estabaActivo.current = active;
  }, [active, lineas]);

  const estado = useMemo(() => {
    return (lineas || []).map((l) => {
      const bruto = pesos[l.order_item_id];
      const peso = bruto === undefined || bruto === '' ? null : Number(bruto);
      const merma = peso != null && Number.isFinite(peso) ? mermaDe(l.quantity, peso) : null;
      const fuera = merma != null && Math.abs(merma) > toleranciaG;
      const listo = peso != null && Number.isFinite(peso) && peso > 0 && !fuera;
      return { ...l, peso, merma, fuera, listo };
    });
  }, [lineas, pesos, toleranciaG]);

  if (!active) return null;
  const pesosListos = estado.length > 0 && estado.every((e) => e.listo);
  // Con pregunta de ubicación, elegir nevera es parte del mismo toque.
  const todoListo = pesosListos && (!ubicacion || neveraElegida != null);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-auto bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Pesar antes de servir"
      {...overlayDismiss}
    >
      <div className="flex w-full max-w-[400px] flex-col gap-4 rounded-lg border border-[var(--border)] bg-[var(--background)] p-6">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h2 className="flex items-center gap-2 text-lg font-medium text-[var(--foreground)]">
              <Scale size={18} strokeWidth={1.5} aria-hidden /> Pesar antes de servir
            </h2>
            <p className="text-sm text-[var(--muted-foreground)]">
              La comanda tiene líneas a peso: pesa cada una en la báscula para poder cobrarla.
              El stock descuenta el peso real.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="-m-1 flex-shrink-0 rounded-sm p-1 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
          >
            <X size={16} strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          {estado.map((l, i) => (
            <div key={l.order_item_id} className="rounded-sm border border-[var(--border)] px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                {/* Nombre entero: es lo que el camarero mira para saber qué está pesando. */}
                <span className="min-w-0 break-words text-sm font-medium text-[var(--foreground)]">
                  {l.product_name || 'Producto'}
                </span>
                <span className="flex-shrink-0 text-sm tabular-nums text-[var(--muted-foreground)]">
                  {Number(l.quantity)} g cobrados
                </span>
              </div>
              <label className="mt-2 flex items-center gap-2 text-xs font-medium text-[var(--muted-foreground)]">
                Báscula
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  autoFocus={i === 0}
                  value={pesos[l.order_item_id] ?? ''}
                  onChange={(e) => setPesos((p) => ({ ...p, [l.order_item_id]: e.target.value }))}
                  placeholder="peso real (g)"
                  data-testid={`bascula-${l.order_item_id}`}
                  className="w-32 rounded-sm border border-[var(--border)] px-2 py-1.5 text-right text-sm tabular-nums text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
                />
              </label>
              {l.fuera && (
                <p className="mt-1.5 text-xs font-medium text-[var(--destructive)]">
                  Merma fuera de rango: {l.merma > 0 ? '+' : ''}{l.merma.toFixed(3)} g.
                  No puede superar ±{toleranciaG.toFixed(2)} g — revisa el peso.
                </p>
              )}
            </div>
          ))}
        </div>

        {ubicacion && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-medium text-[var(--foreground)]">¿De dónde sale?</h3>
              <p className="text-sm text-[var(--muted-foreground)]">
                {ubicacion.product_name || 'El producto'} está en varias ubicaciones. Elige de cuál descontar.
              </p>
            </div>
            {(ubicacion.options || []).map((opt) => {
              const alcanza = opt.enough;
              const elegida = neveraElegida === opt.location_id;
              return (
                <button
                  key={opt.location_id}
                  type="button"
                  data-testid={`ubicacion-${opt.location_id}`}
                  disabled={!alcanza || busy}
                  onClick={() => setNeveraElegida(opt.location_id)}
                  className={`flex w-full items-center justify-between gap-3 rounded-sm border px-4 py-3 text-left transition-colors ${
                    elegida
                      ? 'border-[var(--primary)] bg-[var(--primary-subtle-2)]'
                      : alcanza
                        ? 'cursor-pointer border-[var(--border)] hover:border-[var(--border-hover)] hover:bg-[var(--muted)]'
                        : 'cursor-not-allowed border-[var(--border)] opacity-60'
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="flex-shrink-0 text-[var(--foreground)]">{locationIcon(opt.kind)}</span>
                    {/* «Almacén abace…» no le dice a nadie de qué almacén habla (captura CRUO 24-08). */}
                    <span className="break-words text-sm font-medium text-[var(--foreground)]">{opt.name}</span>
                  </span>
                  <span className="flex flex-shrink-0 items-center gap-2">
                    <span className="text-sm tabular-nums text-[var(--muted-foreground)]">
                      Stock {Number(opt.quantity).toLocaleString('es-ES', { maximumFractionDigits: 3 })} g
                    </span>
                    {!alcanza && (
                      <span className="rounded-full bg-[var(--destructive-bg)] px-2 py-0.5 text-xs font-medium text-[var(--destructive)]">
                        No llega
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={!todoListo || busy}
            onClick={() =>
              onConfirm(
                estado.map((e) => ({ order_item_id: e.order_item_id, real_weight: e.peso })),
                neveraElegida
              )
            }
            className="inline-flex h-9 w-full items-center justify-center rounded-sm bg-[var(--primary)] px-4 text-sm font-medium text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-strong)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Cobrando…' : 'Servir y cobrar'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="inline-flex h-9 w-full items-center justify-center rounded-sm px-4 text-sm font-medium text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)] disabled:opacity-60"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
